import { Suspense, lazy, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Container, Toolbar, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
import TourTaskBanner from '../../sections/welcome-tour/components/TourTaskBanner';
import TourAppBar from '../../sections/welcome-tour/components/TourAppBar';
import PageSpotlight from '../../sections/welcome-tour/components/PageSpotlight';
import SectionWalkthrough from '../../sections/welcome-tour/components/SectionWalkthrough';
import { PAYMENT_WALKTHROUGH } from '../../sections/welcome-tour/copierWalkthrough';
// @ts-ignore - lib/billing is plain JS
import { CHECKOUT_STEPS } from '../../lib/billing/config';
import { useTour } from '../../contexts/TourContext';
import Footer from './Footer';
import useMenuItems from 'menu-items';
import useConfig from 'hooks/useConfig';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import UpgradePlanDrawer from 'sections/account-settings/subscriptions/billing/UpgradePlanDrawer';
import CheckoutDrawerV2 from 'sections/account-settings/subscriptions/checkout-v2/CheckoutDrawer';
import QuickSubscribeDialog from 'sections/get-started/QuickSubscribeDialog';
import PlanRequiredBanner from 'sections/get-started/PlanRequiredBanner';
import PausedPlanBanner from 'features/subscription-freeze/components/PausedPlanBanner';
import { usePausedPurchaseRedirect } from 'features/subscription-freeze/usePausedPurchaseRedirect';
import { useCheckoutStore } from 'sections/account-settings/subscriptions/checkout-v2/store';
import { useUpgradeDrawer } from '../../contexts/UpgradeDrawerContext';
import { featureFlags } from '../../env';
import useAuth from '../../hooks/useAuth';
import { useBillingClient } from '../../client/client-hook';
import { invalidateSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useQuickSubscribe } from '../../sections/get-started/useQuickSubscribe';
import { PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES } from 'lib/billing';

// types
import { RootStateProps } from 'types/root';
import { openDrawer } from 'store/reducers/menu';
import { sizeByBreakpoint } from '../../utils/helper';
import { previewAllowed } from 'features/announcements/preview';
import AnnouncementHost from 'features/announcements/components/AnnouncementHost';
import FallbackBoundary from 'components/FallbackBoundary';

// Dev/staging only, so its sample posts stay out of the production bundle.
const AnnouncementPreview = lazy(() => import('features/announcements/components/AnnouncementPreview'));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('xl'));
  const navigation = useMenuItems();
  const { user, refreshToken } = useAuth() as any;
  const navigate = useNavigate();
  const { isOpen: isUpgradeDrawerOpen, closeUpgradeDrawer, preSelectedPlan } = useUpgradeDrawer();
  const { active: tourActive, phase: tourPhase, current: tourStep, completeStep: completeTourStep } = useTour();
  const tourStepKey = tourStep?.key;
  const tourOnPage = tourActive && tourPhase === 'page';
  const getBillingClient = useBillingClient();
  const storeDrawerOpen = useCheckoutStore((s) => s.drawerOpen);
  const checkoutStep = useCheckoutStore((s) => s.step);
  // The checkout store is the single source of truth for "the drawer is open".
  // Showing the drawer off `isUpgradeDrawerOpen || storeDrawerOpen` meant the
  // upgrade entry points (header chip, locked nav items) opened a drawer the
  // store never knew about — and useInvoicePreviewSync bails on `!drawerOpen`,
  // so those users got no invoice preview, catalog-only totals, and no discount
  // code field at all (it hides itself when the plan reports no purchase ops).
  // Mirror the context flag into the store instead of racing it. Only `true` is
  // pushed: every close path (handleCheckoutClose, handleCheckoutComplete)
  // already clears both, so the drawer can't strand itself open.
  useEffect(() => {
    if (!isUpgradeDrawerOpen) return;
    // "Subscribe to Analyzer" (locked panels, locked nav items) states which
    // product the customer wants — open the drawer with that add-on already on
    // rather than dropping them on the default copier cart to find it.
    if (preSelectedPlan === 'analytics') useCheckoutStore.getState().requestAnalyzerFocus();
    useCheckoutStore.getState().openDrawer();
  }, [isUpgradeDrawerOpen, preSelectedPlan]);
  const quickSubscribeOpen = useQuickSubscribe((s) => s.open);
  // Every purchase surface here, whatever opened it, goes to the pause instead while one is open.
  const paused = usePausedPurchaseRedirect(isUpgradeDrawerOpen || storeDrawerOpen || quickSubscribeOpen, () => {
    closeUpgradeDrawer();
    useCheckoutStore.getState().closeDrawer();
    useQuickSubscribe.getState().close();
  });
  const checkoutOpen = storeDrawerOpen && !paused;

  const returnFromCheckout = (paid: boolean) => {
    // Tour-originated checkouts navigate back instead of stranding the user
    // on the dashboard with the tour silently gone. One-shot by design.
    const dest = useQuickSubscribe.getState().returnTo;
    if (!dest) return;
    useQuickSubscribe.getState().setReturnTo(null);
    // Paid on the plans step: tick it on the way back — the tour shell is not
    // mounted here, so it cannot see the purchase land itself.
    if (paid && tourActive && tourStep?.quickAction === 'subscribe') completeTourStep();
    else navigate(dest);
  };

  // Roles are provisioned by billing on payment, but they only reach the app
  // through a fresh token — so a purchase that grants one (Analyzer, AI
  // Connect) stayed invisible to every role-gated surface until a reload. Ask
  // for a new token a few times after checkout closes, quietly.
  const refreshRolesSoon = () => {
    if (typeof refreshToken !== 'function') return;
    let attempt = 0;
    const tick = () => {
      attempt += 1;
      Promise.resolve(refreshToken())
        .catch(() => undefined)
        .finally(() => {
          if (attempt < 3) window.setTimeout(tick, 4000);
        });
    };
    window.setTimeout(tick, 1500);
  };

  const handleCheckoutClose = () => {
    closeUpgradeDrawer();
    useCheckoutStore.getState().closeDrawer();
    // The drawer may have completed a purchase before closing; refresh the
    // shared billing-derived lock state so the veil/padlocks/banner release
    // without a hard reload.
    invalidateSubscriptionStatus();
    refreshRolesSoon();
    returnFromCheckout(false);
  };

  const handleCheckoutComplete = (_payload?: any) => {
    // Close the drawer(s) and clear the stored completion callback.
    //
    // Do NOT read `store.onComplete` and invoke it here: CheckoutProvider
    // publishes THIS very handler into `store.onComplete` (so PaymentStep can
    // fire it without prop drilling). Calling `store.onComplete()` from inside
    // this handler therefore calls itself, recursing until the stack overflows
    // ("Maximum call stack size exceeded" on a successful purchase). Post-
    // purchase data refresh is handled by SubscriptionsPage reacting to the
    // drawer closing, not by chaining callbacks through the store.
    closeUpgradeDrawer();
    const store = useCheckoutStore.getState();
    store.setOnComplete(null);
    store.closeDrawer();
    // Just paid: refetch + broadcast subscription state so the get-started
    // lock, sidebar padlocks and plan banners release immediately.
    invalidateSubscriptionStatus();
    refreshRolesSoon();
    returnFromCheckout(true);
  };

  const { container, miniDrawer } = useConfig();
  const dispatch = useDispatch();

  const menu = useSelector((state: RootStateProps) => state.menu);
  const { drawerOpen } = menu;

  // State for upgrade drawer data
  const [subscriptionsList, setSubscriptionsList] = useState<any[]>([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [paymentCards, setPaymentCards] = useState<any[]>([]);

  // Fetch billing data when upgrade drawer opens
  useEffect(() => {
    if (isUpgradeDrawerOpen && user?.tcId) {
      getBillingClient().then(async (client) => {
        try {
          const [overview, subList, cardsResp] = await Promise.all([
            client.billingOverview({ userId: user.tcId || '' }),
            client.listSubscriptions({ userId: user.tcId || '', statusList: [...PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES] }),
            client.listCards({ userId: user.tcId || '' })
          ]);
          setCreditBalance((overview as any)?.creditBalance || 0);
          setSubscriptionsList((subList as any).subscriptionsList || []);
          setPaymentCards((cardsResp as any).cardsList || []);
        } catch (error) {
          console.error('Failed to fetch billing data:', error);
        }
      });
    }
  }, [isUpgradeDrawerOpen, user?.tcId, getBillingClient]);

  // drawer toggler
  const [open, setOpen] = useState(!miniDrawer || drawerOpen);
  const handleDrawerToggle = () => {
    const newState = !open;
    setOpen(newState);
    dispatch(openDrawer({ drawerOpen: newState }));
    localStorage.setItem('sidebarOpen', String(newState));
  };

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      setOpen(!matchDownLG);
      dispatch(openDrawer({ drawerOpen: !matchDownLG }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownLG]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* During a tour handoff the tour owns the app bar — the product header
          would otherwise offer navigation the step has deliberately locked. */}
      {tourOnPage ? <TourAppBar /> : <Header open={open} handleDrawerToggle={handleDrawerToggle} />}
      <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />
      <Box
        component="main"
        sx={{
          width: 'calc(100% - 288px)',
          flexGrow: 1,
          px: { xs: 2, sm: 3, md: 4 },
          // Top keeps its breathing room under the fixed header; the bottom was
          // the same 80px (md:10), which read as dead blank space below every
          // page's content on scroll. Trim just the bottom.
          pt: { xs: 2, sm: 3, md: 10 },
          pb: { xs: 2, sm: 3, md: 4 },
          backgroundColor: theme.palette.background.paper,
          // Full-bleed children (e.g. the AI Connect hero banner, which bleeds
          // across this padding with negative margins) must never grow the
          // document's width into blank horizontal scroll. `clip` contains that
          // bleed without becoming a scroll container — so it does NOT break the
          // page's position:sticky elements the way `overflow: hidden` would.
          overflowX: 'clip'
        }}
      >
        <Toolbar sx={{ minHeight: sizeByBreakpoint(0, 0, 0, 0, 56, 64) }} />
        {/* Only renders during a tour handoff onto a real product page. */}
        <TourTaskBanner />
        <PageSpotlight />
        <SectionWalkthrough stepKey={tourStepKey} ready={checkoutStep !== CHECKOUT_STEPS.payment} />
        <SectionWalkthrough stepKey={tourStepKey} stops={PAYMENT_WALKTHROUGH} ready={checkoutStep === CHECKOUT_STEPS.payment} />
        {container && (
          <Container
            maxWidth="lg"
            sx={{
              px: { xs: 0, sm: 2 },
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Breadcrumbs navigation={navigation} title titleBottom card={false} divider={false} />
            <PausedPlanBanner />
            <PlanRequiredBanner />
            <Outlet />
            <Footer />
          </Container>
        )}
        {!container && (
          <Box sx={{ position: 'relative', minHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
            <Breadcrumbs navigation={navigation} title titleBottom card={false} divider={false} />
            <PausedPlanBanner />
            <PlanRequiredBanner />
            <Outlet />
            <Footer />
          </Box>
        )}
      </Box>

      {/* Global Upgrade Plan Drawer */}
      {!featureFlags.newCheckoutUi && (
        <UpgradePlanDrawer
          isOpen={isUpgradeDrawerOpen && !paused}
          onClose={closeUpgradeDrawer}
          onSuccess={() => {
            closeUpgradeDrawer();
          }}
          onCardsUpdated={(cards) => {
            setPaymentCards(cards);
          }}
          subscriptions={subscriptionsList}
          creditBalance={creditBalance}
          paymentCards={paymentCards}
          preSelectedPlan={preSelectedPlan}
        />
      )}
      {featureFlags.newCheckoutUi && (
        <CheckoutDrawerV2 open={checkoutOpen} onClose={handleCheckoutClose} onComplete={handleCheckoutComplete} />
      )}
      {/* Quick two-decision purchase path for unsubscribed users; hands off to
          the drawer's Payment step with the cart pre-staged. */}
      {!paused && <QuickSubscribeDialog />}
      {/* What's New: the launch popup for whatever page someone lands on, by the rules in features/announcements. */}
      <AnnouncementHost />
      {previewAllowed(Boolean(user?.isAdminUser)) && (
        <FallbackBoundary fallback={null}>
          <Suspense fallback={null}>
            <AnnouncementPreview />
          </Suspense>
        </FallbackBoundary>
      )}
    </Box>
  );
};

export default MainLayout;
